'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import titan from '@narvar/titan-client';

interface TitanAnalyticsOptions {
  url: string;
  sourceWriteKey: string;
}

const TitanAnalyticsContext = createContext<TitanAnalyticsOptions | undefined>(
  undefined,
);

export const TitanAnalyticsProvider = TitanAnalyticsContext.Provider;

export function TitanAnalyticsWrapper({
  url,
  children,
}: {
  url: string;
  children: React.ReactNode;
}) {
  return (
    <TitanAnalyticsProvider
      value={{
        url,
        sourceWriteKey: 'narvarone',
      }}
    >
      {children}
    </TitanAnalyticsProvider>
  );
}

const createTitanClient = ({ url, sourceWriteKey }: TitanAnalyticsOptions) =>
  titan({
    url,
    sourceWriteKey,
    fetch: (...args: Parameters<typeof fetch>) => fetch(...args),
    // FIXME - remove reliance on crypto.randomUUID being defined
    anonymousId: process.env.NODE_ENV === 'test' ? 'anonymous id' : undefined,
  });

const useTitanAnalytics = () => {
  const context = useContext(TitanAnalyticsContext);

  if (context === undefined) {
    throw new Error(
      'useTitanAnalytics must be used within a AnalyticsProvider',
    );
  }

  const clientRef = useRef(createTitanClient(context));

  useEffect(() => {
    clientRef.current = createTitanClient({
      url: context.url,
      sourceWriteKey: context.sourceWriteKey,
    });
  }, [clientRef, context.url, context.sourceWriteKey]);

  return clientRef.current;
};

export default useTitanAnalytics;
